import { FC } from 'react';
import styled from 'styled-components';
import ProgressBar from '../ProgressBar/ProgressBar';

const trophyImage = new URL('./assets/trophy.png', import.meta.url).href;
const starImage = new URL('./assets/star.png', import.meta.url).href;

const Container = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    position: relative;
    display: grid;
    grid-template-columns: max-content 35% 13% 1fr;
    grid-template-rows: 1fr 1fr;
    grid-row-gap: 5px;
    padding: 0 0 40px;
    margin: 0 0 33px;

    &.compact {
      grid-template-columns: max-content 1fr;
      width: 70%;
      margin: 0 auto 33px;
      box-shadow: 0px 4px 12px rgba(44, 147, 184, 0.07);
      padding: 0 40px 17px 30px;
      border-radius: 0 0 26px 26px;
    }
    
    &:not(.compact)::after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      bottom: 0;
      left: 0;
      border-bottom: 1px solid ${color.grayscale[700]};
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
    }
  `;
});

const LevelBadge = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    position: relative;
    height: 5.125rem;
    width: 5.125rem;
    color: ${color.grayscale[0]};
    background-color: ${color.accentPrimary.regular};
    border-radius: 50%;
    grid-column: 1;
    grid-row: 1 / span 2;
    margin-right: 23px;

    div {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: ${type.face};
      font-size: ${type.size.tiny};
      font-weight: ${type.weight.bold};
      line-height: 1.25rem;
      text-align: center;

      span {
        font-size: 1.75rem;
        display: block;
      }
    }

    &::before {
      content: '';
      position: absolute;
      display: block;
      height: 3.125rem;
      width: 2.4375rem;
      background: url(${trophyImage});
      left: -1.5rem;
      top: 1.4375rem;
    }
  `;
});

const Title = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-end;
    font-family: ${type.face};
    font-weight: ${type.weight.bold};
    color: ${color.grayscale[0]};
    font-size: ${type.size.regular};
    line-height: 1.1875rem;
    grid-column: 2;
    grid-row: 1;
    text-transform: uppercase;
    z-index: 1;
  `;
});

const ProgressWrapper = styled.div`
  grid-row: 2;
  grid-column: 2;
  position: relative;

  &.with-star::after {
    content: '';
    display: block;
    position: absolute;
    right: -0.75rem;
    top: -0.875rem;
    background: url(${starImage});
    height: 3.0625rem;
    width: 3rem;
    z-index: 2;
  }
`;

const NextTitle = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    font-family: ${type.face};
    font-weight: ${type.weight.bold};
    font-size: ${type.size.small};
    line-height: 1rem;
    grid-column: 4;
    grid-row: 2;
  `;
});

export interface IRewardsHeader {
  compact?: boolean;
  level: number;
  title: string;
  nextTitle?: string;
  progress: number;
  points: number;
  threshold: number;
  hideStar?: boolean;
}

const RewardsHeader: FC<IRewardsHeader> = ({
  compact,
  level,
  title,
  nextTitle,
  progress,
  points,
  threshold,
  hideStar,
}) => {
  const defaultTitle = 'Newcomer';

  return (
    <Container className={compact ? 'compact gamification_tour_intro' : 'gamification_tour_intro'}>
      <LevelBadge>
        <div className="gamification_tour_level">
          LEVEL
          <span>{level}</span>
        </div>
      </LevelBadge>

      <Title>{title ? title : defaultTitle}</Title>

      <ProgressWrapper className={hideStar ? '' : 'with-star'}>
        <ProgressBar percentage={progress} display={`${points} / ${threshold}`} className="gamification_tour_bar" />
      </ProgressWrapper>

      {nextTitle && <NextTitle>Next title: {nextTitle}</NextTitle>}
    </Container>
  );
};

export default RewardsHeader;
